import { Component, Input, SimpleChanges } from '@angular/core';
import { TertiaryStats } from '@athleticnet/athletic-live-api-models';
import { CurrentMeetService } from '../../shared/services/current-meet.service';
import { environment } from 'src/environments/environment';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

@Component({
  selector: 'app-tertiary-stats',
  templateUrl: './tertiary-stats.component.html',
  styleUrls: ['./tertiary-stats.component.scss'],
  imports: [FontAwesomeModule],
})
export class TertiaryStatsComponent {
  @Input() result!: TertiaryStats;
  @Input() showSeed: boolean = false;
  @Input() showHeat: boolean = false;
  @Input() showNotes: boolean = false;
  @Input() showSecondaryStats: boolean = false;
  @Input() showLapsToGo: boolean = false;
  @Input() showAthleteStats: boolean = false;
  @Input() showRecords: boolean = true;
  @Input() showCombinedPoints: boolean = true;
  @Input() showGap: boolean = false;
  @Input() showWind: boolean = true;
  @Input() primaryEventGroup: string = '';

  frontStatLine: string = '';
  middleStatLine: string = '';

  constructor(private currentMeetService: CurrentMeetService) {}

  ngOnInit() {
    this.populateStatLine();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['result'] && !changes['result'].isFirstChange()) {
      this.populateStatLine();
    }
  }

  populateStatLine() {
    const statsString = [];
    const statsWithBackgrounds = [];
    try {
      const showConversions = this.currentMeetService.meet?.showConversions ?? true;
      if (this.result) {
        if (this.showSecondaryStats || this.showNotes) {
          if (this.result.conversion && showConversions) {
            statsString.push(this.result.conversion);
          }
          if ((this.showSecondaryStats || this.showNotes) && this.result.notes) {
            statsString.push(this.result.notes);
          }
        }
        if (this.showLapsToGo && this.result.lapsToGo && this.result.lapsToGo != '0') {
          statsString.push(
            '<strong>' +
              this.result.lapsToGo +
              ' ' +
              $localize`:Laps to go. This will read as the number then "to go"@@tertiaryStats.toGo:to go` +
              '</strong>',
          );
        }
        if (this.showRecords && this.result.records) {
          for (let i = 0, len = this.result.records.length; i < len; i++) {
            const text = this.result.records[i].abbrev ? this.result.records[i].abbrev : this.result.records[i].name;
            statsWithBackgrounds.push('<span class="results-table--record-highlight">' + text + '</span>');
          }
        }
        if (this.showRecords && this.result.timeStandards) {
          for (let i = 0, len = this.result.timeStandards.length; i < len; i++) {
            statsWithBackgrounds.push(
              '<span class="results-table--time_standard-highlight">' + this.result.timeStandards[i].name + '</span>',
            );
          }
        }
        if (this.showRecords && this.result.rankings) {
          for (let i = 0, len = this.result.rankings.length; i < len; i++) {
            statsWithBackgrounds.push(
              '<span class="results-table--ranking-highlight">' + this.result.rankings[i].abbrev + '</span>',
            );
          }
        }
        if (this.result.id === -1) {
          statsWithBackgrounds.push(
            '<span class="results-table--note-highlight">' +
              $localize`:Communicate that a result is unofficial@@tertiaryStats.inProgress:In Progress - Unofficial` +
              '</span>',
          );
        }
        // TODO: Relays
        if (this.result.relayDesignator) {
          statsString.push("'" + this.result.relayDesignator + "'");
        }
        if (this.result.entityRecordNote) {
          statsWithBackgrounds.push(
            '<span class="results-table--entity_record-highlight">' + this.result.entityRecordNote + '</span>',
          );
        }
        if (this.result.reacTime) {
          statsString.push(
            this.result.reacTime + $localize`:Abbreviation for reac time@@tertiaryStats.reacTimeAbbrev:RT`,
          );
        }
        // Always add multi event points
        if (this.showCombinedPoints && this.result.combinedPoints) {
          statsString.push('+' + this.result.combinedPoints);
        }
        if (this.result.points) {
          statsString.push(
            '+' + this.result.points + $localize`:Abbreviation for points scored@@tertiaryStats.pointsScoredAbbrev:pts`,
          );
        }
        if (this.result.wind && this.result.wind !== 'NWI' && this.showWind) {
          let suffix = 'm/s';
          if (this.result.wind.indexOf('/') > -1) {
            // already has m/s tacked on
            suffix = '';
          }
          statsString.push(this.result.wind + suffix);
        }
        // Field event SB
        if (this.showHeat && this.result.heatNumber && this.result.heatNumber != '0') {
          if (
            (this.result.conversion && showConversions) ||
            (this.primaryEventGroup && this.primaryEventGroup && this.primaryEventGroup === 'Field')
          ) {
            statsString.push(
              $localize`:Abbreviation for flight@@tertiaryStats.flightAbbrev:F` + this.result.heatNumber,
            );
          } else {
            statsString.push($localize`:Abbreviation for heat@@tertiaryStats.heatAbbrev:H` + this.result.heatNumber);
          }
        }
        if (this.result.athlete) {
          if (this.result.athlete.compNum && this.currentMeetService.meet?.showCompNums) {
            statsString.push('#' + this.result.athlete.compNum);
          }

          if (this.result.athlete.age) {
            statsString.push($localize`:@@tertiaryStats.ageLabel:Age` + ': ' + this.result.athlete.age);
          }

          if (this.result.athlete.year) {
            statsString.push(
              $localize`:Grade year abbreviation@@tertiaryStats.yearLabel:Yr` + ': ' + this.result.athlete.year,
            );
          }
        }
        if (this.result.compNum && this.currentMeetService.meet?.showCompNums) {
          statsString.push('#' + this.result.compNum);
        }

        if (this.showGap && this.result.gap) {
          statsString.push(this.result.gap);
        }

        if (this.result.age) {
          statsString.push($localize`:@@tertiaryStats.ageLabel2:Age` + ': ' + this.result.age);
        }

        if (this.result.year) {
          statsString.push($localize`:Grade year abbreviation@@tertiaryStats.yearLabel:Yr` + ': ' + this.result.year);
        }
        if (this.showAthleteStats) {
          if (this.result.personalRecord) {
            statsString.push(
              $localize`:Personal Record abbreviation@@tertiaryStats.personalRecordLabel:PR` +
                ': ' +
                this.result.personalRecord,
            );
          }
          if (this.result.seasonRecord) {
            statsString.push(
              $localize`:Season Best abbreviation@@tertiaryStats.seasonBestLabel:SB` + ': ' + this.result.seasonRecord,
            );
          }
        }
        if (this.result.relayNames) {
          statsString.push(this.result.relayNames);
        }
        if (this.result.relayMembers) {
          var relayMemberString = [];
          for (var i = 0, len = this.result.relayMembers.length; i < len; i++) {
            // Old meets only have the name variable
            var firstName = this.result.relayMembers[i].athlete.firstName[0];
            var lastName = this.result.relayMembers[i].athlete.lastName;
            relayMemberString.push(firstName + '. ' + lastName);
          }
          if (relayMemberString.length > 0) {
            statsString.push(relayMemberString.join(', '));
          }
        }

        if (statsWithBackgrounds.length > 0) {
          this.frontStatLine = statsWithBackgrounds.join(' ');
        }

        if (statsString.length > 0) {
          this.middleStatLine = statsString.join(' • ');

          if (this.frontStatLine) {
            this.middleStatLine = ' • ' + this.middleStatLine;
          }
        } else {
          this.middleStatLine = '';
        }
      }
    } catch (error) {
      console.error(`Error populating tertiary stats`, error);
    }
  }

  protected readonly environment = environment;
}
